import { useEffect, useRef, useState, useCallback } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IoIosArrowRoundDown } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Typewriter from 'typewriter-effect';
import Button from '../../Component/Button/Button';
import Footer from '../../Component/Footer';
import Header from '../../Component/Header';
import PropertyCard from '../../Component/PropertyCard';
import LeaveChatModal from '../../modals/LeaveChatModal/LeaveChatModal';
import classes from './chatBot.module.css';
import { HiOutlineRefresh } from 'react-icons/hi';

// Custom hook to block navigation

function ChatBot() {
  const { selectedLanguage } = useSelector((state) => state?.languageReducer);
  const [typewriterState, setTypewriterState] = useState({});
  const { t } = useTranslation('chatBot');
  const navigate = useNavigate();
  const { isLogin } = useSelector((state) => state.authReducer);
  const [messages, setMessages] = useState(() => [
    {
      id: 1,
      type: 'received',
      content: t('welcomeMessage'),
      timestamp: new Date(),
      sender: 'bot'
    }
  ]);
  const [botProperties, setBotProperties] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const messagesEndRef = useRef(null);
  const propertySectionRef = useRef(null);
  const originalNavigate = useRef(navigate);

  // Custom navigation function that shows confirmation modal
  const safeNavigate = useCallback((to, options) => {
    setPendingNavigation({ to, options });
    setShowAreYouSureModal(true);
  }, []);

  // Store the original navigate function for safe navigation
  useEffect(() => {
    originalNavigate.current = navigate;
  }, [navigate]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTo({
        top: messagesEndRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  const scrollToProperties = useCallback(() => {
    propertySectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
    setTypewriterState({});
  }, [messages, scrollToBottom]);

  const sendMessage = useCallback(async () => {
    if (inputMessage.trim() === '') return;
    const userMessage = {
      id: Date.now(),
      type: 'sent',
      content: inputMessage,
      timestamp: new Date(),
      sender: 'user'
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    try {
      const response = await fetch('https://turegu-ai-api.devcustomprojects.online/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: inputMessage, limit: 20 })
      });
      const data = await response.json();
      console.log(data);
      let botContent = '';
      if (data?.success) {
        const city = data.filters_used?.city || data.city || '';
        const total = data.total_found ?? 0;
        if (total === 0) {
          botContent = city
            ? `${t('noPropertiesFoundIn')} ${city}.`
            : // : t('noPropertiesFoundGeneric');
              data?.message || t('noPropertiesFoundGeneric');
          setBotProperties([]);
        } else if (Array.isArray(data.properties) && data.properties.length > 0) {
          const firstLine =
            total === 1
              ? city
                ? `${t('found')} 1 ${t('propertyIn')} ${city}:`
                : `${t('found')} 1 ${t('property')}.`
              : city
              ? `${t('found')} ${total} ${t('propertiesIn')} ${city}:`
              : `${t('found')} ${total} ${t('properties')}.`;
          botContent = [
            firstLine,
            ...data.properties.map((p) => `• ${p?.title?.[selectedLanguage]}`)
          ];
          setBotProperties(data.properties);
        } else {
          botContent =
            total === 1
              ? city
                ? `${t('found')} 1 ${t('propertyIn')} ${city}.`
                : `${t('found')} 1 ${t('property')}.`
              : city
              ? `${t('found')} ${total} ${t('propertiesIn')} ${city}.`
              : `${t('found')} ${total} ${t('properties')}.`;
          setBotProperties([]);
        }
      } else if (data?.error) {
        botContent = data.error;
        setBotProperties([]);
      } else if (data?.message) {
        botContent = data.message;
        setBotProperties([]);
      }
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'received',
          content: botContent,
          timestamp: new Date(),
          sender: 'bot'
        }
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'received',
          content: 'Sorry, there was an error connecting to the server.',
          timestamp: new Date(),
          sender: 'bot'
        }
      ]);
      setBotProperties([]);
    }
    setIsTyping(false);
  }, [inputMessage, t]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter') sendMessage();
    },
    [sendMessage]
  );

  // Handle page refresh and tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    const handleKeyDown = (e) => {
      // Detect Ctrl+R, F5, or Ctrl+Shift+R for refresh
      if (
        (e.ctrlKey && e.key === 'r') ||
        e.key === 'F5' ||
        (e.ctrlKey && e.shiftKey && e.key === 'R')
      ) {
        e.preventDefault();
        setShowAreYouSureModal(true);
        setPendingAction('refresh');
      }
    };

    const handleContextMenu = (e) => {
      // Prevent right-click context menu to avoid refresh option
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Handle browser back button and refresh button
  useEffect(() => {
    const handlePopState = (e) => {
      e.preventDefault();
      setShowAreYouSureModal(true);
      setPendingAction('back');
      // Push the current state back to prevent navigation
      window.history.pushState(null, '', window.location.pathname);
    };

    const handleVisibilityChange = () => {
      // This helps detect when user might be trying to refresh
      // We can't completely prevent refresh button clicks, but we can warn
      if (document.visibilityState === 'hidden') {
        // Page is being hidden, might be refresh
        // We'll keep the beforeunload handler for this case
      }
    };

    // Push initial state
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handle modal confirmation
  const handleModalConfirm = useCallback(() => {
    setShowAreYouSureModal(false);

    if (pendingAction === 'back') {
      // Allow back navigation
      window.history.go(-1);
    } else if (pendingAction === 'refresh') {
      // Allow page refresh
      window.location.reload();
    } else if (pendingNavigation) {
      // Allow programmatic navigation
      originalNavigate.current(pendingNavigation.to, pendingNavigation.options);
      setPendingNavigation(null);
    }

    setPendingAction(null);
  }, [pendingAction, pendingNavigation]);

  // Handle modal cancel
  const handleModalCancel = useCallback(() => {
    setShowAreYouSureModal(false);
    setPendingAction(null);
    setPendingNavigation(null);
  }, []);

  // Safe refresh function
  const handleSafeRefresh = useCallback(() => {
    setShowAreYouSureModal(true);
    setPendingAction('refresh');
  }, []);

  return (
    <>
      <Header safeNavigate={safeNavigate} />
      <Container className={classes.container}>
        <Row>
          <Col md={12}>
            <div className={classes.head}>
              <div className={classes.breadcrumb}>
                <p
                  onClick={(e) => {
                    e.preventDefault();
                    safeNavigate('/');
                  }}>
                  {t('breadcrumb.home')}
                </p>
                <span className={classes.separator}>›</span>
                <p data-active="true">{t('breadcrumb.chatBot')}</p>
              </div>
            </div>
            <div className={classes.chatBotContainer}>
              <div className={classes.chatHeader}>
                <h1 className={classes.title}>{t('title')}</h1>
                {isLogin && (
                  <button
                    onClick={handleSafeRefresh}
                    className={classes.refreshButton}
                    title="Refresh Chat">
                    <HiOutlineRefresh color="var(--white)" />
                  </button>
                )}
              </div>
              {!isLogin ? (
                <div className={classes.loginPrompt}>
                  <div className={classes.loginPromptContent}>
                    <div className={classes.loginIcon}>🔒</div>
                    <h3>{t('loginRequired.title')}</h3>
                    <p>{t('loginRequired.message')}</p>
                    <div className={classes.loginButtons}>
                      <Button
                        variant="primary"
                        onClick={() => navigate('/login')}
                        className={classes.loginButton}>
                        {t('loginRequired.loginButton')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/signup')}
                        className={classes.signupButton}>
                        {t('loginRequired.signupButton')}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={classes.chatInterface}>
                  <div ref={messagesEndRef} className={classes.messagesContainer}>
                    {messages.map((message, msgIdx) => {
                      const isLatestBotMsg =
                        message.type === 'received' && msgIdx === messages.length - 1;
                      return (
                        <div
                          key={message.id}
                          className={`${classes.message} ${
                            message.type === 'sent' ? classes.sentMessage : classes.receivedMessage
                          }`}>
                          <div className={classes.messageContent}>
                            {message.type === 'received' ? (
                              Array.isArray(message.content) ? (
                                isLatestBotMsg ? (
                                  message.content.map((line, idx) => {
                                    if (typewriterState[message.id] === undefined) {
                                      if (idx === 0) {
                                        return (
                                          <Typewriter
                                            key={idx}
                                            options={{ delay: 15, cursor: '' }}
                                            onInit={(typewriter) => {
                                              typewriter
                                                .typeString(line)
                                                .callFunction(() => {
                                                  setTypewriterState((prev) => ({
                                                    ...prev,
                                                    [message.id]: 1
                                                  }));
                                                })
                                                .start();
                                            }}
                                          />
                                        );
                                      }
                                      return null;
                                    } else if (typewriterState[message.id] > idx) {
                                      return <div key={idx}>{line}</div>;
                                    } else if (typewriterState[message.id] === idx) {
                                      return (
                                        <Typewriter
                                          key={idx}
                                          options={{ delay: 15, cursor: '' }}
                                          onInit={(typewriter) => {
                                            typewriter
                                              .typeString(line)
                                              .callFunction(() => {
                                                setTypewriterState((prev) => ({
                                                  ...prev,
                                                  [message.id]: idx + 1
                                                }));
                                              })
                                              .start();
                                          }}
                                        />
                                      );
                                    }
                                    return null;
                                  })
                                ) : (
                                  message.content.map((line, idx) => <div key={idx}>{line}</div>)
                                )
                              ) : isLatestBotMsg ? (
                                <Typewriter
                                  options={{ delay: 15, cursor: '' }}
                                  onInit={(typewriter) => {
                                    typewriter.typeString(message.content).start();
                                  }}
                                />
                              ) : (
                                message.content
                              )
                            ) : (
                              message.content
                            )}
                          </div>
                          {message.type === 'received' &&
                            botProperties.length > 0 &&
                            isLatestBotMsg && (
                              <div className={classes.chatArrowBtn}>
                                <button
                                  onClick={scrollToProperties}
                                  className={classes.chatArrowBtns}>
                                  <IoIosArrowRoundDown size={20} /> {t('viewProperties')}
                                </button>
                              </div>
                            )}
                          <div className={classes.messageTime}>
                            {message.timestamp.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {isTyping && (
                      <div className={classes.receivedMessage}>
                        <div className={classes.messageContent}>
                          <div className={classes.typingIndicator}>
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                          {t('typing')}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={classes.inputContainer}>
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={t('inputPlaceholder')}
                      className={classes.messageInput}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={inputMessage.trim() === ''}
                      className={classes.sendButton}>
                      {t('sendButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>
          <div ref={propertySectionRef} style={{ width: '100%' }}>
            {botProperties.length > 0 && (
              <Row md={12}>
                {botProperties.map((property, idx) => (
                  <Col
                    className={classes.propertyCard}
                    xs={12}
                    md={6}
                    lg={4}
                    key={property._id || idx}>
                    <PropertyCard
                      outerNavigate={() => window.open(`/property/${property.slug}`, '_blank')}
                      data={property}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Row>
      </Container>
      <Footer safeNavigate={safeNavigate} showDownload={false} />
      {showAreYouSureModal && (
        <LeaveChatModal
          show={showAreYouSureModal}
          setShow={handleModalCancel}
          onClick={handleModalConfirm}
          onHide={handleModalCancel}
          onClick1={handleModalCancel}
        />
      )}
    </>
  );
}

export default ChatBot;
